import ModalWrapper from 'dashboard/components/ModalWrapper';
import { ModalBody, ModalContainer, ModalFooter, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import { CheckName } from 'lib/verificationCheck';

export const CHECK_WARNING_MODAL_TITLE = 'Unrequire this verification check?';

// Checks which surface an explicit warning when a user unrequires them, as they should be
// required for 99% of use cases: https://www.notion.so/withpersona/Bond-Internal-Give-an-are-you-sure-popup-in-FLE-when-the-user-wants-to-unrequire-certain-verifi-d44b031915d64edabf6a5369850082bd?pvs=4#79f2a27086e24a4b8a19ea1222187c6a
export const EXPLICIT_WARNING_TO_UNREQUIRE_CHECKS = [
  // Database Checks
  CheckName.DatabaseBirthdateComparison,
  CheckName.DatabaseDeceasedDetection,
  CheckName.DatabaseIdentityComparison,
  CheckName.DatabaseNameComparison,
  CheckName.DatabaseSocialSecurityNumberComparison,
  // Document Checks
  CheckName.DocumentCompromisedDetection,
  CheckName.DocumentUnprocessableSubmissionDetection,
  // Gov ID Checks
  CheckName.IdCompromisedDetection,
  CheckName.IdDisallowedCountryDetection,
  CheckName.IdDisallowedTypeDetection,
  CheckName.IdEntityDetection,
  CheckName.IdInconsistentRepeatDetection,
  CheckName.IdInquiryComparison,
  CheckName.IdMrzDetection,
  CheckName.IdPortraitDetection,
  CheckName.IdUnprocessableSubmissionDetection,
  // Selfie Checks
  CheckName.SelfieIdComparison,
  CheckName.SelfieLivenessDetection,
  CheckName.SelfieSuspiciousEntityDetection,
] as const;

export type ExplicitUnrequireWarningCheck = (typeof EXPLICIT_WARNING_TO_UNREQUIRE_CHECKS)[number];

export interface ExplicitWarningCheck extends VerificationCheckConfig {
  check: ExplicitUnrequireWarningCheck;
}

export function isExplicitWarningCheck(
  verificationCheck: VerificationCheckConfig | undefined,
): verificationCheck is ExplicitWarningCheck {
  switch (verificationCheck?.check) {
    case CheckName.DatabaseBirthdateComparison:
    case CheckName.DatabaseDeceasedDetection:
    case CheckName.DatabaseIdentityComparison:
    case CheckName.DatabaseNameComparison:
    case CheckName.DatabaseSocialSecurityNumberComparison:
    case CheckName.DocumentCompromisedDetection:
    case CheckName.DocumentUnprocessableSubmissionDetection:
    case CheckName.IdCompromisedDetection:
    case CheckName.IdDisallowedCountryDetection:
    case CheckName.IdDisallowedTypeDetection:
    case CheckName.IdEntityDetection:
    case CheckName.IdInconsistentRepeatDetection:
    case CheckName.IdInquiryComparison:
    case CheckName.IdMrzDetection:
    case CheckName.IdPortraitDetection:
    case CheckName.IdUnprocessableSubmissionDetection:
    case CheckName.SelfieIdComparison:
    case CheckName.SelfieLivenessDetection:
    case CheckName.SelfieSuspiciousEntityDetection:
      return true;
    default:
      return false;
  }
}

export const WARNING_CHECK_DESCRIPTION_MAP: Record<ExplicitUnrequireWarningCheck, string> = {
  // Database Checks
  [CheckName.DatabaseBirthdateComparison]:
    'Persona recommends requiring this check, as a birthdate is considered a core identity component.',
  [CheckName.DatabaseDeceasedDetection]:
    'Persona recommends requiring this check, as it detects if the information provided match the records of a deceased person (US only).',
  [CheckName.DatabaseIdentityComparison]:
    'Persona recommends requiring this check, as it detects that the identity matches a record in an authoritative or issuing database (US only).',
  [CheckName.DatabaseNameComparison]:
    'Persona recommends requiring this check, as a name is considered a core identity component.',
  [CheckName.DatabaseSocialSecurityNumberComparison]:
    'Persona recommends requiring this check, as an identification number is considered a core identity component.',
  // Document Checks
  [CheckName.DocumentCompromisedDetection]:
    'Persona recommends requiring this check, as it detects if the submission can be found from a publicly available source.',
  [CheckName.DocumentUnprocessableSubmissionDetection]:
    'Persona recommends always requiring this check, as an unprocessable document will result in unreliable verification results.',
  // Gov ID Checks
  [CheckName.IdCompromisedDetection]:
    'Persona recommends requiring this check, as it will detect if the submission is a sample ID, publicly available, or has otherwise been reported as fraudulent.',
  [CheckName.IdDisallowedCountryDetection]:
    'Persona recommends requiring this check, as it detects if a user is submitting an ID from a country that is disallowed.',
  [CheckName.IdDisallowedTypeDetection]:
    'Persona recommends requiring this check, as it will detect if the submitted ID type is not the same as what the user selected.',
  [CheckName.IdEntityDetection]:
    'Persona recommends requiring this check, as it detects that the submission includes a Government ID.',
  [CheckName.IdInconsistentRepeatDetection]:
    'Persona recommends requiring this check, as it detects if we have previously seen the same ID portrait with different details, or the same ID details with a different face portrait.',
  [CheckName.IdInquiryComparison]:
    'Persona recommends requiring this check when the user’s information is prefilled on the inquiry, as it detects that the information on the ID provided matches the initial values given.',
  [CheckName.IdMrzDetection]:
    'Persona recommends requiring this check, as it ensures that any Machine Readable Zone expected on an ID is detected.',
  [CheckName.IdPortraitDetection]:
    'Persona recommends requiring this check, as it detects that there is a portrait on the ID.',
  [CheckName.IdUnprocessableSubmissionDetection]:
    'Persona recommends always requiring this check, as an unprocessable document will result in unreliable verification results.',
  // Selfie Checks
  [CheckName.SelfieIdComparison]:
    'Persona recommends requiring this check, as it detects if the selfie submitted matches the portrait on the ID submitted.',
  [CheckName.SelfieLivenessDetection]:
    'Persona recommends requiring this check, as it detects if the selfie contains a live individual and not a recording, picture, or other spoof.',
  [CheckName.SelfieSuspiciousEntityDetection]:
    'Persona recommends requiring this check, as it detects if the selfie submission is that of a Government ID.',
};

type CheckWarningModalProps = {
  checkName: ExplicitUnrequireWarningCheck | undefined;
  isOpen: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
};

const CheckWarningModal = ({
  checkName,
  isOpen,
  handleClose,
  handleConfirm,
}: CheckWarningModalProps) => {
  return (
    <ModalWrapper isOpen={isOpen} handleCloseModal={handleClose}>
      <ModalContainer>
        <ModalHeader onClose={handleClose}>{CHECK_WARNING_MODAL_TITLE}</ModalHeader>
        <ModalBody>
          {checkName && <Paragraph>{WARNING_CHECK_DESCRIPTION_MAP[checkName]}</Paragraph>}
        </ModalBody>
        <ModalFooter>
          <Button
            id='check-warning-modal__cancel-button'
            variant='basic'
            aria-label='Cancel'
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            id='check-warning-modal__confirm-button'
            variant='danger'
            aria-label='Confirm'
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalWrapper>
  );
};

export default CheckWarningModal;
